"use client";

import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

interface DayEntry {
  value: string;
  label: string;
  day: number;
}

interface Props {
  date: string;
  todayValue: string;
  threeDayStrip: DayEntry[];
  onDateChange: (next: string) => void;
  onShiftDay: (delta: number) => void;
  onAIPlan: () => void;
  aiPlanLoading: boolean;
  aiPlanStatusText: string | null;
  aiPlanLongRunning: boolean;
  onCancelAIPlan: () => void;
  onAddTimebox: () => void;
  sideContextChildren: ReactNode;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

export default function PlanRightRail({
  date,
  todayValue,
  threeDayStrip,
  onDateChange,
  onShiftDay,
  onAIPlan,
  aiPlanLoading,
  aiPlanStatusText,
  aiPlanLongRunning,
  onCancelAIPlan,
  onAddTimebox,
  sideContextChildren,
  collapsed,
  onToggleCollapsed,
}: Props) {
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const datePopoverRef = useRef<HTMLDivElement | null>(null);
  const [sideOpen, setSideOpen] = useState(true);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  useEffect(() => {
    if (collapsed) setSideOpen(false);
  }, [collapsed]);

  useEffect(() => {
    if (!datePickerOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!datePopoverRef.current) return;
      if (!datePopoverRef.current.contains(event.target as Node)) {
        setDatePickerOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDatePickerOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [datePickerOpen]);

  if (collapsed) {
    return (
      <aside
        className="hidden xl:flex xl:sticky xl:top-4 flex-col items-center gap-2 rounded-3xl border border-border/60 bg-card/70 px-2 py-3 shadow-sm"
        data-testid="plan-right-rail-collapsed"
      >
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label="Expand right panel"
          title="Expand right panel"
          className="flex h-9 w-9 items-center justify-center rounded-xl text-text-light transition hover:bg-secondary/60 hover:text-text"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className="my-1 h-px w-6 bg-border" />
        <button
          type="button"
          onClick={() => onShiftDay(-1)}
          aria-label="Previous day"
          title="Previous day"
          className="flex h-9 w-9 items-center justify-center rounded-xl text-text-light transition hover:bg-secondary/60 hover:text-text"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => onDateChange(todayValue)}
          aria-label="Jump to today"
          title="Jump to today"
          className="flex h-9 w-9 items-center justify-center rounded-xl bg-forest-med/10 text-forest-med text-[10px] font-semibold transition hover:bg-forest-med/15"
        >
          {new Date(`${date}T12:00:00`).getDate()}
        </button>
        <button
          type="button"
          onClick={() => onShiftDay(1)}
          aria-label="Next day"
          title="Next day"
          className="flex h-9 w-9 items-center justify-center rounded-xl text-text-light transition hover:bg-secondary/60 hover:text-text"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
        <div className="my-1 h-px w-6 bg-border" />
        <button
          type="button"
          onClick={onAIPlan}
          disabled={aiPlanLoading}
          aria-label={aiPlanLoading ? "AI Time Box planning" : "AI Time Box"}
          title="AI Time Box"
          data-testid="schedule-ai-timebox-button"
          className="flex h-9 w-9 items-center justify-center rounded-xl bg-forest-med text-white transition hover:bg-forest disabled:opacity-50"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2l1.6 4.4L18 8l-4.4 1.6L12 14l-1.6-4.4L6 8l4.4-1.6L12 2z" />
            <path d="M19 14l.7 1.9 1.9.7-1.9.7-.7 1.9-.7-1.9-1.9-.7 1.9-.7.7-1.9z" />
            <path d="M5.5 15l.5 1.4 1.4.5-1.4.5-.5 1.4-.5-1.4L3.6 17l1.4-.5.5-1.4z" />
          </svg>
        </button>
        {aiPlanLoading && (
          <button
            type="button"
            onClick={onCancelAIPlan}
            aria-label="Cancel AI Time Box"
            title="Cancel AI Time Box"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-white text-text-light transition hover:bg-secondary/60 hover:text-text"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
        <button
          type="button"
          onClick={onAddTimebox}
          aria-label="Add timebox"
          title="Add timebox"
          data-testid="schedule-add-timebox-button"
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-white text-text-light transition hover:bg-secondary/60 hover:text-text"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      </aside>
    );
  }

  return (
    <aside
      className="hidden xl:block xl:sticky xl:top-4"
      data-testid="plan-right-rail"
    >
      <div className="rounded-3xl border border-border/60 bg-card/70 p-4 shadow-sm space-y-4">
        {/* Header: collapse toggle */}
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted">
            Plan day
          </p>
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label="Collapse right panel"
            title="Collapse right panel"
            data-testid="plan-right-rail-collapse"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-muted transition hover:bg-secondary/60 hover:text-text"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        {/* 3-day pager + date picker */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => onShiftDay(-1)}
              aria-label="Previous day"
              className="flex h-9 w-7 shrink-0 items-center justify-center rounded-lg text-text-light transition hover:bg-secondary/60 hover:text-text"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="flex flex-1 gap-1.5">
              {threeDayStrip.map((day) => {
                const active = day.value === date;
                const isToday = day.value === todayValue;
                return (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => onDateChange(day.value)}
                    className={`flex flex-1 flex-col items-center rounded-xl border px-1.5 py-2 transition-colors ${
                      active
                        ? "border-forest bg-forest-med text-white"
                        : "border-border bg-bg text-text-light hover:bg-secondary"
                    }`}
                  >
                    <span className={`text-[9px] font-semibold uppercase tracking-[0.08em] ${active ? "text-white/80" : "text-muted"}`}>
                      {isToday ? "Today" : day.label}
                    </span>
                    <span className="text-[13px] font-semibold">{day.day}</span>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => onShiftDay(1)}
              aria-label="Next day"
              className="flex h-9 w-7 shrink-0 items-center justify-center rounded-lg text-text-light transition hover:bg-secondary/60 hover:text-text"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onDateChange(todayValue)}
              disabled={date === todayValue}
              className="flex-1 rounded-lg border border-border bg-white px-2 py-1.5 text-[11px] font-semibold text-text-light transition hover:bg-secondary/60 hover:text-text disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-text-light"
            >
              Jump to today
            </button>
            <div className="relative" ref={datePopoverRef}>
              <button
                type="button"
                onClick={() => setDatePickerOpen((current) => !current)}
                aria-label="Pick a date"
                aria-expanded={datePickerOpen}
                title="Pick a date"
                data-testid="plan-right-rail-date-picker"
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition ${
                  datePickerOpen
                    ? "border-forest-med bg-sage-pale text-forest-med"
                    : "border-border bg-white text-text-light hover:bg-secondary/60 hover:text-text"
                }`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </button>
              {datePickerOpen && (
                <div
                  className="absolute right-0 top-9 z-30 w-[240px] rounded-xl border border-border bg-card p-3 shadow-lg"
                  data-testid="plan-right-rail-date-popover"
                >
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
                      Pick a date
                    </p>
                    <button
                      type="button"
                      onClick={() => setDatePickerOpen(false)}
                      aria-label="Close date picker"
                      className="flex h-6 w-6 items-center justify-center rounded-md text-muted transition hover:bg-secondary/60 hover:text-text"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                  <input
                    ref={dateInputRef}
                    type="date"
                    value={date}
                    onChange={(e) => {
                      if (e.target.value) {
                        onDateChange(e.target.value);
                        setDatePickerOpen(false);
                      }
                    }}
                    className="w-full rounded-lg border border-border bg-white px-2 py-1.5 text-sm text-text focus:border-forest-med focus:outline-none"
                    autoFocus
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* AI Time Box + Add timebox */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onAIPlan}
            disabled={aiPlanLoading}
            aria-describedby={aiPlanLoading ? "plan-right-rail-ai-status" : undefined}
            data-testid="schedule-ai-timebox-button"
            className="flex h-10 items-center justify-center rounded-xl bg-forest-med px-3 text-xs font-semibold text-white transition hover:bg-forest disabled:opacity-50"
          >
            {aiPlanLoading ? "Planning..." : "AI Time Box"}
          </button>
          <button
            type="button"
            onClick={onAddTimebox}
            data-testid="schedule-add-timebox-button"
            className="flex h-10 items-center justify-center rounded-xl border border-border bg-white px-3 text-xs font-medium text-text-light transition hover:bg-secondary/60 hover:text-text"
          >
            Add timebox
          </button>
        </div>
        {aiPlanLoading && aiPlanStatusText && (
          <div
            className={`rounded-2xl border px-3 py-3 ${
              aiPlanLongRunning
                ? "border-amber/25 bg-amber/10"
                : "border-border/70 bg-bg/80"
            }`}
            data-testid="plan-right-rail-ai-status"
          >
            <p
              id="plan-right-rail-ai-status"
              role="status"
              aria-live="polite"
              className="text-[11px] leading-relaxed text-text-light"
            >
              {aiPlanStatusText}
            </p>
            <button
              type="button"
              onClick={onCancelAIPlan}
              className="mt-2 rounded-full border border-border bg-white px-3 py-1.5 text-[11px] font-semibold text-text-light transition hover:bg-secondary/60 hover:text-text"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Side Context */}
        <div className="border-t border-border/60 pt-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted">
                Side context
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-text-light">
                Life weighting and deeper planning cues.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSideOpen((current) => !current)}
              className="shrink-0 rounded-full border border-border/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-light transition hover:border-forest/30 hover:text-text"
              data-testid="plan-right-rail-side-toggle"
            >
              {sideOpen ? "Hide" : "Show"}
            </button>
          </div>
          {sideOpen && (
            <div className="mt-3 space-y-4">{sideContextChildren}</div>
          )}
        </div>
      </div>
    </aside>
  );
}
